import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Shield, Truck, MapPin, CheckCircle2, QrCode, Landmark, Wallet, Loader2, FileText } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { fileUrl, formatIDR, downloadOrderInvoice } from "@/lib/api";
import { exportBuyerInvoicePdf } from "@/lib/settlementPdf";

const COURIERS = [
  { code: "JNT", label: "J&T Express", eta: "2-3 hari", cost: 0 },
  { code: "SICEPAT", label: "SiCepat", eta: "1-2 hari", cost: 0 },
  { code: "GOSEND", label: "GoSend Instant", eta: "Hari ini", cost: 0 },
];

const iconFor = (icon) => ({ "qr-code": QrCode, landmark: Landmark, wallet: Wallet }[icon] || Landmark);

export default function Checkout() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [methods, setMethods] = useState([]);
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState("");
  const [courier, setCourier] = useState("JNT");
  const [method, setMethod] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const handleDownloadInvoice = async () => {
    if (!order) return;
    setDownloadingInvoice(true);
    try {
      try {
        await downloadOrderInvoice(order.id, order.mch_order_no);
      } catch {
        await exportBuyerInvoicePdf(order);
      }
      toast.success("Invoice pembelian berhasil diunduh");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunduh invoice");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => setProduct(r.data));
    api.get("/payments/methods").then((r) => { setMethods(r.data); setMethod(r.data[0]?.code); });
  }, [productId]);

  const createOrder = async () => {
    if (!address.trim()) return toast.error("Isi alamat pengiriman");
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { product_id: productId, courier, address, payment_method: method });
      setOrder(data);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat pesanan");
    } finally { setLoading(false); }
  };

  const confirmPay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/confirm`);
      setOrder(data);
      setStep(3);
      toast.success("Pembayaran berhasil!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal konfirmasi");
    } finally { setLoading(false); }
  };

  if (!product) return <div className="py-20 text-center text-gray-400">Memuat...</div>;

  const selectedMethod = methods.find((m) => m.code === method);

  return (
    <div className="max-w-lg mx-auto">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 text-sm mb-4">
        <ChevronLeft className="w-4 h-4" /> Kembali
      </button>

      {/* Steps */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1.5 rounded-full transition-all ${step >= s ? "bg-[#0B4DA2] w-10" : "bg-gray-200 w-6"}`} />
        ))}
      </div>

      {/* Product summary */}
      <div className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100 bg-white mb-5">
        <img src={fileUrl((product.images || [])[0])} alt="" className="w-16 h-16 rounded-xl object-cover" />
        <div className="flex-1">
          <div className="font-semibold text-sm text-gray-900 line-clamp-1">{product.title}</div>
          <div className="text-[#0B4DA2] font-extrabold">{formatIDR(product.price)}</div>
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="text-sm font-semibold text-gray-800 flex items-center gap-1 mb-2"><MapPin className="w-4 h-4" /> Alamat Pengiriman</label>
            <textarea data-testid="checkout-address-input" value={address} onChange={(e) => setAddress(e.target.value)} rows={3} placeholder="Nama, no HP, alamat lengkap..." className="w-full px-4 py-3 rounded-xl border border-gray-200 outline-none focus:border-[#0B4DA2] text-sm resize-none" />
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-800 flex items-center gap-1 mb-2"><Truck className="w-4 h-4" /> Kurir</label>
            <div className="space-y-2">
              {COURIERS.map((c) => (
                <button key={c.code} data-testid={`courier-${c.code}`} onClick={() => setCourier(c.code)} className={`w-full flex items-center justify-between p-3 rounded-xl border text-left ${courier === c.code ? "border-[#0B4DA2] bg-[#EFF6FF]" : "border-gray-200"}`}>
                  <div>
                    <div className="font-semibold text-sm">{c.label}</div>
                    <div className="text-xs text-gray-500">Estimasi {c.eta}</div>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600">GRATIS</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-gray-800 mb-2 block">Metode Pembayaran</label>
            <div className="space-y-2">
              {methods.map((m) => {
                const Icon = iconFor(m.icon);
                return (
                  <button key={m.code} data-testid={`payment-${m.code}`} onClick={() => setMethod(m.code)} className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left ${method === m.code ? "border-[#0B4DA2] bg-[#EFF6FF]" : "border-gray-200"}`}>
                    <Icon className="w-5 h-5 text-[#0B4DA2]" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{m.label}</div>
                    </div>
                    {method === m.code && <CheckCircle2 className="w-5 h-5 text-[#0B4DA2]" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white border border-gray-100 space-y-1 text-sm">
            <Row label="Harga barang" value={formatIDR(product.price)} />
            <Row label="Biaya admin" value={formatIDR(2500)} />
            <Row label="Ongkir" value="Gratis" />
            <div className="border-t border-gray-100 my-1" />
            <Row label="Total" value={formatIDR(product.price + 2500)} bold />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-sm text-emerald-800">
            <Shield className="w-5 h-5 shrink-0" /> Dilindungi <b>Kalabiru Safe Pay</b>. Dana diteruskan ke penjual setelah barang diterima.
          </div>

          {method === "TARAPAY_QRIS" && product.price + 2500 > 10000000 && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="qris-limit-notice">
              <b>Maksimal scan QRIS Rp 10.000.000 per transaksi.</b> Total {formatIDR(product.price + 2500)} akan dipecah menjadi {Math.ceil((product.price + 2500) / 10000000)} transaksi QRIS — scan semuanya satu per satu saat pembayaran.
            </div>
          )}

          <button data-testid="checkout-pay-button" onClick={createOrder} disabled={loading} className="w-full py-3.5 rounded-xl bg-[#0B4DA2] hover:bg-[#083B7C] text-white font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Lanjut Bayar {formatIDR(product.price + 2500)}
          </button>
        </div>
      )}

      {step === 2 && order && (
        <div className="space-y-4">
          <div className="p-5 rounded-2xl bg-white border border-gray-100 text-center">
            <div className="text-xs text-gray-400 uppercase">{selectedMethod?.label}</div>
            <div className="text-2xl font-extrabold text-[#0B4DA2] my-2">{formatIDR(order.amount)}</div>
            {(order.split_parts || []).length > 0 && (
              <div className="text-left my-3">
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 mb-3">
                  <b>Maksimal scan QRIS Rp 10.000.000 per transaksi.</b> Pembayaran dipecah menjadi {order.split_parts.length} transaksi — scan semuanya satu per satu.
                </div>
                {order.split_parts.map((part) => (
                  <div key={part.index} className="mb-4 pb-4 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                    <div className="flex items-center justify-between text-sm font-semibold mb-2">
                      <span>Transaksi {part.index}/{order.split_parts.length}</span>
                      <span className="text-[#0B4DA2]">{formatIDR(part.amount)}</span>
                    </div>
                    {part.qr_string ? (
                      <div className="mx-auto w-fit"><QRCodeSVG value={part.qr_string} size={200} level="M" /></div>
                    ) : (
                      <div className="w-44 h-44 mx-auto bg-gray-900 rounded-xl flex items-center justify-center">
                        <QrCode className="w-20 h-20 text-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!order.split_parts?.length && order.payin.payment_type === "qris" && (
              order.payin.qr_string ? (
                <div className="mx-auto w-fit my-3">
                  <QRCodeSVG value={order.payin.qr_string} size={220} level="M" />
                  <div className="text-[10px] text-gray-400 mt-1">Scan dengan aplikasi e-wallet / m-banking</div>
                </div>
              ) : (
                <div className="mx-auto w-44 h-44 bg-gray-900 rounded-xl flex items-center justify-center my-3">
                  <QrCode className="w-24 h-24 text-white" />
                </div>
              )
            )}
            <div className="text-xs text-amber-600 bg-amber-50 rounded-lg py-2 px-3 mt-2">Bayar dalam {order.payin.expires_in_minutes} menit • ID: {order.payin.trx_id}</div>
          </div>

          <p className="text-center text-xs text-gray-400">Scan QR lalu tekan tombol di bawah untuk cek pembayaran.</p>

          <button data-testid="confirm-payment-button" onClick={confirmPay} disabled={loading} className="w-full py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} Cek Status Pembayaran
          </button>
        </div>
      )}

      {step === 3 && order && (
        <div className="text-center py-8" data-testid="order-success">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-900">Pembayaran Berhasil!</h2>
          <p className="text-sm text-gray-500 mt-1">Pesanan kamu sedang diproses penjual.</p>
          <div className="mt-5 p-4 rounded-2xl bg-white border border-gray-100 text-left text-sm space-y-1">
            <Row label="Total dibayar" value={formatIDR(order.amount)} bold />
            <Row label="Kurir" value={order.courier} />
            <Row label="Status" value="Dibayar" />
          </div>
          <div className="mt-5 space-y-2.5">
            <button
              data-testid="checkout-download-invoice"
              onClick={handleDownloadInvoice}
              disabled={downloadingInvoice}
              className="w-full py-3.5 rounded-xl border-2 border-[#0B4DA2] bg-white text-[#0B4DA2] hover:bg-blue-50 font-semibold transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {downloadingInvoice ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Unduh Invoice PDF
            </button>
            <button onClick={() => navigate("/")} className="w-full py-3.5 rounded-xl bg-[#0B4DA2] text-white font-semibold hover:bg-[#093d82] transition">
              Kembali Belanja
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className={bold ? "font-bold text-gray-900" : "text-gray-500"}>{label}</span>
    <span className={bold ? "font-extrabold text-[#0B4DA2]" : "text-gray-700"}>{value}</span>
  </div>
);
